import type { Map as MapLibreMap } from "maplibre-gl";
import { generateDynamicMarker, getRingStyleForPosition } from "@wondermaps/maps/browser";
import { resolvePoiIcon, type ThemeConfig, type PoiCategory } from "@wondermaps/maps";
import { MARKER_ICONS } from "../../data/marker-icons";
import { getFontAwesomeSvgData } from "../../data/fontawesome-icons";
import type { POICategory } from "../MapCanvas";
import type { MarkerIconData } from "@wondermaps/maps/browser";

/** Info about a sponsor tier for ring sprite generation */
export interface SponsorTierInfo {
  color: string; // hex color e.g. "#FFD700"
  position: number; // 1 = most prominent, 2, 3+
}

/**
 * Generate and add base POI marker sprites (no sponsor rings).
 * Rings are added lazily post-init via addSponsorRingSprites().
 */
export function addDynamicMarkers(
  map: MapLibreMap,
  theme: ThemeConfig,
  apiCategories: POICategory[]
): void {
  // Generate base markers for theme-defined categories
  for (const [slug, categoryConfig] of Object.entries(theme.poiCategories)) {
    const imageId = `poi-${slug}`;
    if (map.hasImage(imageId)) continue;
    const faIconData = getFontAwesomeSvgData(categoryConfig.icon);
    const iconData = faIconData ?? MARKER_ICONS[slug as PoiCategory] ?? MARKER_ICONS.utilities;
    const imageData = generateDynamicMarker(theme.poi, iconData, categoryConfig.circleColor, undefined, theme.shadowDirection);
    map.addImage(imageId, imageData, { pixelRatio: window.devicePixelRatio });
  }

  // Generate base markers for API categories not already handled by theme config
  for (const cat of apiCategories) {
    const imageId = `poi-${cat.slug}`;
    if (map.hasImage(imageId)) continue;
    const iconName = resolvePoiIcon(cat.slug, theme, cat.icon);
    const faIconData = getFontAwesomeSvgData(iconName);
    const iconData = faIconData ?? MARKER_ICONS[cat.slug as PoiCategory] ?? MARKER_ICONS.utilities;
    const imageData = generateDynamicMarker(theme.poi, iconData, cat.color, undefined, theme.shadowDirection);
    map.addImage(imageId, imageData, { pixelRatio: window.devicePixelRatio });
  }

  // Fallback icon for uncategorized POIs
  if (!map.hasImage("poi-map-pin")) {
    const fallbackIcon = getFontAwesomeSvgData("map-pin") ?? MARKER_ICONS.utilities;
    const imageData = generateDynamicMarker(theme.poi, fallbackIcon, "#6B7280", undefined, theme.shadowDirection);
    map.addImage("poi-map-pin", imageData, { pixelRatio: window.devicePixelRatio });
  }
}

/**
 * Generate sponsor ring sprites lazily post-init.
 * Creates poi-{slug}-ring-{hexNoHash} sprites for each category × tier color.
 * Call after map.idle / requestIdleCallback so map is interactive first.
 */
export function addSponsorRingSprites(
  map: MapLibreMap,
  theme: ThemeConfig,
  apiCategories: POICategory[],
  sponsorTiers: SponsorTierInfo[]
): void {
  if (sponsorTiers.length === 0) return;

  // Collect all category slugs that need ring variants
  const categorySlugs: Array<{ slug: string; iconData: MarkerIconData; circleColor?: string }> = [];

  for (const [slug, categoryConfig] of Object.entries(theme.poiCategories)) {
    const faIconData = getFontAwesomeSvgData(categoryConfig.icon);
    const iconData = faIconData ?? MARKER_ICONS[slug as PoiCategory] ?? MARKER_ICONS.utilities;
    categorySlugs.push({ slug, iconData, circleColor: categoryConfig.circleColor });
  }

  for (const cat of apiCategories) {
    // Skip if already in theme categories
    if (categorySlugs.some((c) => c.slug === cat.slug)) continue;
    const iconName = resolvePoiIcon(cat.slug, theme, cat.icon);
    const faIconData = getFontAwesomeSvgData(iconName);
    const iconData = faIconData ?? MARKER_ICONS[cat.slug as PoiCategory] ?? MARKER_ICONS.utilities;
    categorySlugs.push({ slug: cat.slug, iconData, circleColor: cat.color });
  }

  // Add fallback
  const fallbackIcon = getFontAwesomeSvgData("map-pin") ?? MARKER_ICONS.utilities;
  categorySlugs.push({ slug: "map-pin", iconData: fallbackIcon, circleColor: "#6B7280" });

  // Generate ring sprites for each category × tier color
  for (const tier of sponsorTiers) {
    const hexKey = tier.color.replace("#", "");
    const ringStyle = getRingStyleForPosition(tier.position, tier.color);

    for (const cat of categorySlugs) {
      const spriteId = `poi-${cat.slug}-ring-${hexKey}`;
      if (map.hasImage(spriteId)) continue;
      const imageData = generateDynamicMarker(
        theme.poi,
        cat.iconData,
        cat.circleColor,
        ringStyle,
        theme.shadowDirection
      );
      map.addImage(spriteId, imageData, { pixelRatio: window.devicePixelRatio });
    }
  }
}
